import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUserStats } from '../../context/UserStatsContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { PLAN_NAMES } from '../../types';

const UserProfileDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { stats } = useUserStats();
  const { plan, isActive } = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const totalSessions = stats
    ? stats.grammarSessions + stats.readingSessions + stats.listeningSessions + stats.speakingSessions
    : 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-10 h-10 rounded-full bg-kids-primary text-white flex items-center justify-center hover:bg-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-kids-primary focus:ring-offset-2"
      >
        <span className="text-lg font-bold">
          {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-2xl shadow-xl dark:shadow-slate-900 border border-gray-100 dark:border-slate-700 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Signed in as</p>
            <p className="font-medium text-gray-800 dark:text-white truncate">{currentUser?.email}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                plan === 'kids' && isActive
                  ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'
              }`}>
                {PLAN_NAMES[plan]}
              </span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-2">Quick Stats</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                  {formatTime(stats?.totalPracticeMinutes || 0)}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-300">Practice Time</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{totalSessions}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">Sessions</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{stats?.currentStreak || 0}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">Day Streak</p>
              </div>
              <div className="bg-pink-50 dark:bg-pink-900/30 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-pink-600 dark:text-pink-400">{stats?.longestStreak || 0}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">Best Streak</p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/app/profile');
              }}
              className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-3"
            >
              <span className="text-xl">{'\u{1F464}'}</span>
              <span>My Profile</span>
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/pricing');
              }}
              className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center gap-3"
            >
              <span className="text-xl">{'\u{1F4B3}'}</span>
              <span>Subscription</span>
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 dark:border-slate-700 pt-1">
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3"
            >
              <span className="text-xl">{'\u{1F6AA}'}</span>
              <span>Log Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;
